import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface LanguageSwitcherProps {
  transparent?: boolean;
}

const languages = [
  { code: 'en', label: 'English', flag: 'GB' },
  { code: 'pt-br', label: 'Português', flag: 'BR' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
];

export default function LanguageSwitcher({ transparent = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
    const pathMap: Record<string, string> = {
      'en': '/en',
      'pt-br': '/pt-br',
      'de': '/de',
    };
    navigate(pathMap[code] || '/en');
  };

  const textClass = transparent ? 'text-background-50' : 'text-foreground-800';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md hover:bg-background-100/80 transition-colors ${textClass}`}
      >
        <span className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center bg-background-200">
          <span className="text-xs font-bold">{currentLang.flag}</span>
        </span>
        <span className="hidden lg:inline">{currentLang.label}</span>
        <i className={`ri-arrow-down-s-line transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-background-50 rounded-lg border border-background-200/70 shadow-lg z-50 overflow-hidden">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-background-100 transition-colors ${
                i18n.language === lang.code ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700'
              }`}
            >
              <span className="w-5 h-5 rounded-full overflow-hidden flex items-center justify-center bg-background-200">
                <span className="text-xs font-bold">{lang.flag}</span>
              </span>
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}